import { SafeUrl } from '@angular/platform-browser';
import { TimelineNode } from '@shared/components/timeline/timeline.model';
import { Models } from 'appwrite';

export interface Property extends Models.Document {
  visible: boolean;
  name: string;
  price: number;
  images: Image[];
  videoTour: string;
  virtualTour: string;
  city: string;
  country?: Country;
  addressCommunity: string;
  spaces: Space[];
  investementStatus: InvestementStatus;
  state: State;
  netYieldPercentage: number;
  totalReturnPercentage: number;
  totalInvested: number;
  totalInvestedPercentage: number;
  propertyNumber: number;
  category: Category;
  numberOfBedrooms: number;
  investmentStrategy?: InvestmentStrategy;

  // New fields
  area?: number;
  rentalType?: RentalType;
  grossYieldPercentage?: number;
  numberOfInvestors?: number;
  totalViews?: number;
  description?: string;
  documents?: Document[];
  location?: Location;
  neighbourhood?: Neighbourhood;
  fundingTimeline?: FundingTimelineEvent[];
  isBookmarked?: boolean;
  appreciation?: number; // We use this in the cart

  // Financials
  annualRent?: number;
  transactionCost?: number;
  investmentCost?: number;
  annualServiceCharges?: number;
  annualManagementMaintenance?: number;
  netIncome?: number;
  renovationScope?: RenovationScope;

  // Only for Sold properties
  grossTotalRentalIncome?: number;
  grossSellPrice?: number;
  fundedDate?: Date;
  exitDate?: Date;

  // Only for Funded properties
  latestValuation?: number;

  // Not part of the model
  // Just to manage the state locally
  fundingTimelineComponent?: TimelineNode[];
}

export interface BookedmarkedProperty extends Models.Document {
  property: Property;
}

export interface PropertyParams {
  status: string;
  states: string[];
  categories: string[];
  investmentStrategyTypes: string[];
  page: number;
  itemsPerPage: number;
}

interface Image {
  id: string;
  url: string | SafeUrl;
  caption?: string;
  thumbnail?: string;
  resolutions?: {
    main: string;
    original: string;
    thumbnail: string;
  };
}

interface Space {
  type: SpaceType;
  count: number;
}

interface Country {
  name: string;
  shortName: string;
  icon: string;
}

interface InvestmentStrategy {
  type: InvestmentStrategyType;
  name: string;
  description?: string;
  highlights?: InvestmentStrategyHighlights[];
}

interface InvestmentStrategyHighlights {
  title: string;
  description: string;
}

export interface Document {
  name: string;
  type: FileType;
  url: string;
  canBeDeleted?: boolean;
}

interface Location {
  lat: number;
  lng: number;
}

interface Neighbourhood {
  name: string;
  url: string;
  description: string;
}

interface RenovationScope {
  renovationBudget: number;
  operationalBudget: number;
  projectedAppreciation: number;
  projectedUnutilisedFunds: number;
  projectedExitCostsWithVat: number;
  projectedExitPrice: number;
  description: string;
  renovationDurationMinMonths: number;
  renovationDurationMaxMonths: number;
}

interface FundingTimelineEvent {
  type: FundingTimelineEventType;
  expectedAt: Date;
  completedAt?: Date;
  projectedAt?: Date;
}

export interface PropertyViewPageConfiguration {
  description: boolean;
  howItWorks: boolean;
  investmentStrategy: boolean;
  investmentCalculator: boolean;
  propertyOverview: boolean;
  renovations: boolean;
  leasingStrategy: boolean;
  financialsGeneric: boolean;
  financialsRenovation: boolean;
  funingTimeline: boolean;
  location: boolean;
  documents: boolean;
  moreQuestions: boolean;
}

export enum InvestementStatus {
  AVAILABLE = 'available',
  FUNDED = 'funded',
  EXITED = 'exited',
}

export enum State {
  READY = 'ready',
  RENOVATION = 'renovation',
  SOLD = 'sold',
  CONSTRUCTION = 'construction',
  OPERATIONAL = 'operational',
}

export enum Category {
  RESIDENTIAL = 'residential',
  COMMERCIAL = 'commercial',
  INDUSTRIAL = 'industrial',
  LAND = 'land',
  ENERGY = 'energy',
  TRANSPORTATION = 'transportation',
}

export enum ResidentialType {
  APARTMENT = 'apartment',
  STUDIO = 'studio',
  VILLA = 'villa',
  AGRICULTURAL = 'agricultural',
  ENERGY = 'energy',
}

export enum SpaceType {
  BEDROOM = 'bedroom',
  BATH = 'bath',
  LIVING_ROOM = 'living-room',
  KITCHEN = 'kitchen',
  STUDIO = 'studio',
  AGRICULTURAL = 'agricultural',
}

export enum RentalType {
  LONG_TERM_RENTAL = 'long-term-rental',
  HOLIDAY_RENTAL = 'holiday-rental',
}

export enum InvestmentStrategyType {
  BALANCED = 'balanced',
  CAPITAL_GROWTH = 'capital-growth',
  HIGH_YIELD = 'high-yield',
  FIX_N_FLIP = 'fix-n-flip',
  GOLDEN_VISA = 'golden-visa',
}

export enum FileType {
  PDF = 'pdf',
  DOC = 'doc',
  DOCX = 'docx',
  XLS = 'xls',
  XLSX = 'xlsx',
  PPT = 'ppt',
  PPTX = 'pptx',
  TXT = 'txt',
  RTF = 'rtf',
  ODT = 'odt',
}

export enum MimeFileType {
  PDF = 'application/pdf',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  PPT = 'application/vnd.ms-powerpoint',
  PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  TXT = 'text/plain',
  RTF = 'application/rtf',
  ODT = 'application/vnd.oasis.opendocument.text',
}

export const mimeStringToFileTypeMap: Record<string, FileType> = {
  'application/pdf': FileType.PDF,
  'application/msword': FileType.DOC,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    FileType.DOCX,
  'application/vnd.ms-excel': FileType.XLS,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    FileType.XLSX,
  'application/vnd.ms-powerpoint': FileType.PPT,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    FileType.PPTX,
  'text/plain': FileType.TXT,
  'application/rtf': FileType.RTF,
  'application/vnd.oasis.opendocument.text': FileType.ODT,
};

export enum FundingTimelineEventType {
  FUNDING_DATE = 'funding-date',
  DOCUMENT_DISTRIBUTION = 'document-distribution',
  FIRST_RENTAL_PAYMENT = 'first-rental-payment',
  RENOVATION = 'renovation',
  REVALUATION_AND_LISTED = 'revaluation-and-listed',
  EXPECTED_SALE = 'expected-sale',
}
